/* eslint-disable react/no-unescaped-entities */
/* This example requires Tailwind CSS v2.0+ */
import { Menu, Transition } from '@headlessui/react';
import clsx from 'clsx';
import { Fragment, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import config from '../../config';
import { useAllKitties } from '../../hooks/useAllKitties';
import { useCitiesStore } from '../../hooks/useCitiesStore';
import { useUserAssetsStore } from '../../hooks/useUserAssetsStore';
import NftImage from '../NftImage';
import Spinner from '../Spinner';
import formatNumber from '../../utils/formatNumber';

export default function CollabBlockStakingFormSelector({ type, label, collab }) {
  const { userAssets } = useUserAssetsStore();
  const { allKitties } = useAllKitties();
  const { allCities } = useCitiesStore();
  const { watch, setValue } = useFormContext();
  const selectedNft = watch(`staked.${type}`);
  const [options, setOptions] = useState(null);

  const tokenImage = collab.isToken && collab.assets[0] ? `/collab-token/${collab.assets[0]}.png` : '';
  const tokenOptionDetails = collab.isToken && type === 'collab' ? options?.[0] : null;
  const tokenAmount = tokenOptionDetails ? tokenOptionDetails.amount : null;
  const requiredAmount = collab.isToken ? collab.requiredAssetAmount : null;

  useEffect(() => {
    if (!userAssets || !allKitties) return;

    const collabAssetIds = Object.values(config.collabBlocks).find((c) => c.id === collab.id).assets;
    let newOptions = null;

    if (type === 'collab') {
      newOptions = userAssets.filter((a) => collabAssetIds.includes(a['asset-id'])).filter((a) => a.amount >= requiredAmount);
    } else if (type === 'kitty') {
      const assetIds = userAssets.map((a) => a['asset-id']);
      newOptions = allKitties.filter((k) => assetIds.includes(k.id));
    } else if (type === 'city') {
      const assetIds = userAssets.map((a) => a['asset-id']);
      newOptions = allCities.filter((k) => assetIds.includes(k.index));
    }

    // Only update state if options have changed
    if (JSON.stringify(newOptions) !== JSON.stringify(options)) {
      setOptions(newOptions);
    }
  }, [userAssets, allKitties, allCities, type, options, collab, tokenOptionDetails, requiredAmount]);

  if (!options) {
    return (
      <div className='relative text-left aspect-square flex items-center justify-center px-10 bg-red-500 rounded-lg'>
        <p className='text-center'>You don't have the correct NFTs for this collab.</p>
      </div>
    );
  }

  return (
    <div>
      <Menu as='div' className='relative text-left w-full'>
        <Menu.Button
          disabled={options?.length === 0}
          className={`relative text-xl font-bold aspect-square flex items-center justify-center rounded-lg w-[250px] h-[250px] bg-gray-800 overflow-hidden`}
        >
          {selectedNft ? (
            <>
              {tokenImage && tokenOptionDetails ? (
                <img src={tokenImage} alt='Token' className='absolute w-full h-full inset-0 object-cover rounded-md object-cover' />
              ) : (
                <NftImage nft={selectedNft} className='rounded-lg object-cover absolute inset-0 w-full h-full' />
              )}
            </>
          ) : (
            <p className='flex flex-col px-5'>
              <span>Select a</span> <span className='break-all'>{label}</span>
            </p>
          )}
        </Menu.Button>

        {selectedNft && (
          <div className='flex flex-col gap-0.5 mt-2 text-center'>
            <strong className='text-gray-50 text-xl font-bold'>{selectedNft.name}</strong>
            <span className='text-gray-500 text-sm'>{selectedNft.assetId}</span>
            {type === 'collab' && typeof tokenAmount === 'number' && (
              <span className='text-gray-500 text-sm'>
                {formatNumber(tokenAmount / 10 ** 6)} {collab.collection}
              </span>
            )}
          </div>
        )}

        <Transition
          as={Fragment}
          enter='transition ease-out duration-100'
          enterFrom='transform opacity-0 scale-95'
          enterTo='transform opacity-100 scale-100'
          leave='transition ease-in duration-75'
          leaveFrom='transform opacity-100 scale-100'
          leaveTo='transform opacity-0 scale-95'
        >
          <Menu.Items className='absolute top-full left-0 z-[999] max-h-[250px] overflow-y-scroll mt-2 origin-bottom-left rounded-lg shadow-lg bg-neutral-800 ring-1 ring-black ring-opacity-5 focus:outline-none overflow-x-hidden'>
            <div className='py-1'>
              {options?.map((n, index) => (
                <CollabBlockStakingFormSelectorButton
                  nft={n}
                  tokenImage={!!tokenOptionDetails ? tokenImage : null}
                  key={n['asset-id']}
                  onSelect={(selected) => setValue(`staked.${type}`, selected)}
                />
              ))}
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  );
}

const CollabBlockStakingFormSelectorButton = ({ nft, tokenImage, onSelect }) => {
  const assetId = nft['asset-id'] || nft.id || nft.index;
  const [nftDetails, setNftDetails] = useState(null);

  const name = nftDetails?.params?.name;

  const onClick = () => {
    onSelect({ assetId, name });
  };

  useEffect(() => {
    if (!assetId) return;
    const getNftDetails = async () => {
      const resp = await fetch(`https://mainnet-idx.algonode.cloud/v2/assets/${assetId}`);
      const json = await resp.json();
      setNftDetails(json.asset);
    };
    getNftDetails();
  }, [assetId]);

  return (
    <Menu.Item>
      <button
        type='button'
        className={clsx('flex items-center w-full px-4 py-2 space-x-5 text-sm text-left text-gray-300 hover:bg-gray-900')}
        onClick={onClick}
      >
        <div className='relative w-[80px] h-[80px] bg-gray-500 overflow-hidden rounded-md flex-shrink-0'>
          {tokenImage ? (
            <img src={tokenImage} alt='Token' className='absolute w-full h-full inset-0 object-cover rounded-md object-cover' />
          ) : (
            nftDetails && <NftImage nft={nftDetails} className='absolute w-full h-full inset-0 object-cover rounded-md object-cover' />
          )}

          {!nftDetails && (
            <div className='absolute inset-0 flex items-center justify-center'>
              <Spinner className='w-8 h-8' />
            </div>
          )}
        </div>

        <div>
          <div className='flex flex-col font-medium'>
            <span>{name}</span>
          </div>
        </div>
      </button>
    </Menu.Item>
  );
};
